import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/cn';
import { BodySkeleton } from '../card-skeleton/card-skeleton';

const pillVariants = cva('rounded-full px-2 py-0.5 text-[12px] font-semibold', {
	variants: {
		tone: {
			warn: 'bg-warn/20 text-warn',
			accent: 'bg-accent/20 text-accent',
			purple: 'bg-accent-purple/20 text-accent-purple',
			success: 'bg-accent-green/20 text-accent-green',
			danger: 'bg-danger/20 text-danger',
			muted: 'bg-text-muted/20 text-text-muted',
		},
	},
	defaultVariants: {
		tone: 'muted',
	},
});

export interface PillProps extends VariantProps<typeof pillVariants> {
	/** Testo/contenuto della pillola. */
	children: React.ReactNode;
}

/** Pillola arrotondata colorata, per ruoli o stati in una tabella di amministrazione. */
const Pill = ({ tone, children }: PillProps) => (
	<span className={pillVariants({ tone })}>{children}</span>
);
Pill.displayName = 'Pill';

export interface AdminTableColumn<Row> {
	/** Chiave univoca della colonna (usata come React key). */
	key: string;
	/** Intestazione mostrata nel thead. */
	header: React.ReactNode;
	/** Allineamento del testo della colonna. */
	align?: 'left' | 'right' | 'center';
	/** Trattamento tipografico della cella (testo normale, monospaziato o attenuato). */
	variant?: 'default' | 'mono' | 'muted';
	/** Funzione che renderizza il contenuto della cella per una riga. */
	render: (row: Row, index: number) => React.ReactNode;
}

export interface AdminTableProps<Row> {
	/** Definizione delle colonne della tabella. */
	columns: AdminTableColumn<Row>[];
	/** Righe di dati da visualizzare. */
	rows: Row[];
	/** Funzione che estrae una chiave React univoca da una riga. */
	rowKey: (row: Row) => string | number;
}

/** Tabella di amministrazione incorniciata in una card, con celle spaziose e intestazioni maiuscole. */
function AdminTable<Row>({ columns, rows, rowKey }: AdminTableProps<Row>) {
	const alignCls = (align?: 'left' | 'right' | 'center') =>
		align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : undefined;

	return (
		<div className="rounded-xl border border-border bg-card">
			<table className="w-full text-left text-xs">
				<thead>
					<tr className="border-b border-border text-[12px] uppercase tracking-wider text-text-muted">
						{columns.map((col) => (
							<th key={col.key} className={cn('px-4 py-3', alignCls(col.align))}>
								{col.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr key={rowKey(row)} className="border-b border-border/50 last:border-b-0">
							{columns.map((col) => (
								<td
									key={col.key}
									className={cn(
										'px-4 py-3',
										alignCls(col.align),
										col.variant === 'mono' && 'font-mono text-text',
										col.variant === 'muted' && 'text-text-muted',
									)}
								>
									{col.render(row, i)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
AdminTable.displayName = 'AdminTable';

export interface AdminTableSkeletonProps<Row> {
	/** Le stesse colonne della {@link AdminTable} reale: le intestazioni restano vere, non c'è bisogno di ridichiarare i titoli. */
	columns: AdminTableColumn<Row>[];
	/** Numero di righe placeholder. Default: 5. */
	rows?: number;
}

/**
 * Skeleton di {@link AdminTable}: stessa cornice e stesse intestazioni reali
 * (le passa chi la usa — sono statiche, non c'è un dato da aspettare), righe
 * skeleton al posto delle celle.
 */
function AdminTableSkeleton<Row>({ columns, rows = 5 }: AdminTableSkeletonProps<Row>) {
	const alignCls = (align?: 'left' | 'right' | 'center') =>
		align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : undefined;

	return (
		<div
			className="rounded-xl border border-border bg-card"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico (`div`), stesso pattern di PanelCardSkeleton/DistributionBar.
			role="status"
			aria-label="Caricamento…"
		>
			<table className="w-full text-left text-xs">
				<thead>
					<tr className="border-b border-border text-[12px] uppercase tracking-wider text-text-muted">
						{columns.map((col) => (
							<th key={col.key} className={cn('px-4 py-3', alignCls(col.align))}>
								{col.header}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{Array.from({ length: rows }).map((_, i) => (
						<tr key={i} className="border-b border-border/50 last:border-b-0">
							{columns.map((col) => (
								<td key={col.key} className={cn('px-4 py-3', alignCls(col.align))}>
									<BodySkeleton className="h-3 w-full max-w-32" />
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
AdminTableSkeleton.displayName = 'AdminTableSkeleton';

export { AdminTable, AdminTableSkeleton, Pill, pillVariants };
